export default function GeographicSection() {
  return (
    <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
            El Paso Land Buyers: We Buy Throughout the Region
          </h2>
          <p className="text-xl text-teal-50 max-w-3xl mx-auto leading-relaxed">
            TRex RE LLC purchases land throughout El Paso County, Texas and Doña Ana County, New Mexico. Here's what we look for in different property types.
          </p>
        </div>

        <div className="mb-16">
          <div className="w-full h-96 rounded-lg overflow-hidden shadow-2xl">
            <img
              src="https://public.readdy.ai/ai/img_res/b4d421f6ca35f486625976b1f9116e5d.jpg"
              alt="El Paso region land types"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="bg-white rounded-lg p-8 shadow-xl">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
              <i className="ri-community-line text-teal-600 text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">Infill Lots & Urban Land</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>What we buy:</strong> Residential lots in established El Paso neighborhoods, typically 0.1-1 acre. These are vacant lots surrounded by existing homes.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Why they're valuable:</strong> Infill lots have utilities, paved access, and are in desirable locations. Perfect for custom home builders or homeowners wanting to build their dream home.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Common issues we handle:</strong> Odd shapes, small sizes, easement problems, or title complications that make traditional sales difficult.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Typical offer range:</strong> $15,000-$75,000 depending on location, size, and utilities.
            </p>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-xl">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
              <i className="ri-landscape-line text-teal-600 text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">Rural Acreage</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>What we buy:</strong> Large parcels (5-100+ acres) in outlying areas. Agricultural land, ranch land, or undeveloped acreage.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Why they're valuable:</strong> Long-term investment potential, recreational use, future development, or resale to homesteaders and ranchers.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Common issues we handle:</strong> No utilities, dirt road access, remote locations, or properties that have been on the market for years without selling.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Typical offer range:</strong> $1,000-$5,000 per acre depending on location and access.
            </p>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-xl">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
              <i className="ri-building-4-line text-teal-600 text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">Commercial Land</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>What we buy:</strong> Commercially zoned land on major roads and highways. Suitable for retail, office, or mixed-use development.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Why they're valuable:</strong> High traffic counts, visibility, and development potential make commercial land valuable to businesses and developers.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Common issues we handle:</strong> Environmental concerns, access limitations, or properties requiring expensive site work before development.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Typical offer range:</strong> $40,000-$150,000+ per acre on major corridors.
            </p>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-xl">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
              <i className="ri-home-gear-line text-teal-600 text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">Problem Properties</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>What we buy:</strong> Land with title issues, liens, boundary disputes, landlocked parcels, or other complications that prevent traditional sales.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Why they're valuable:</strong> We have the expertise and resources to resolve complex issues. What's a nightmare for you is a solvable problem for us.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Common issues we handle:</strong> Clouded title, tax liens, probate complications, boundary disputes, or properties multiple realtors have refused to list.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Typical offer range:</strong> Varies widely based on the specific issues and resolution costs.
            </p>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 border-2 border-white/20">
          <h3 className="text-3xl font-bold mb-6 text-white text-center">We Buy All Property Types</h3>
          <p className="text-xl text-teal-50 text-center max-w-3xl mx-auto leading-relaxed mb-8">
            Whether you own a small city lot or 100 acres of ranch land, we're interested. We evaluate every property individually and make fair offers based on current market conditions and the property's unique characteristics.
          </p>
          <div className="text-center">
            <a href="tel:+1-915-206-5814" className="inline-flex items-center space-x-2 px-8 py-4 bg-white text-teal-600 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-phone-fill"></i>
              <span>Call Now: (915) 206-5814</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}